import { useEffect, useReducer } from 'react';

import combineToLegacyCompatibleServices from './combineToLegacyCompatibleServices';
import { loadPrivateServices, loadPublicServices } from './loadServicesNew';

import {
  PublicAndPrivateServices,
  PublicAndPrivateServicesPayload,
  ServiceLoaderResults,
} from './types';
import { Service } from '@zapier/services/esm/types';

type State = {
  isLoadingPublicApps?: boolean;
  isLoadingPrivateApps?: boolean;
  errorFetchingPublicApps?: Error | null | undefined;
  errorFetchingPrivateApps?: Error | null | undefined;
  services: PublicAndPrivateServices;
};

const initialState: State = {
  isLoadingPublicApps: true,
  isLoadingPrivateApps: true,
  errorFetchingPublicApps: null,
  errorFetchingPrivateApps: null,
  services: {
    publicServices: [],
    privateServices: [],
  },
};

type TypeOnlyAction = {
  type: 'loading' | 'load-fail-public' | 'load-fail-private';
};

type ActionWithPayload = PublicAndPrivateServicesPayload & {
  type: 'load-success-public' | 'load-success-private';
};

type Action = TypeOnlyAction | ActionWithPayload;

const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'loading':
      return {
        isLoadingPublicApps: true,
        isLoadingPrivateApps: true,
        errorFetchingPublicApps: null,
        errorFetchingPrivateApps: null,
        services: state.services, // keep using previous services
      };
    case 'load-success-public':
      return {
        ...state,
        isLoadingPublicApps: false,
        errorFetchingPublicApps: null,
        services: {
          publicServices: action.publicServices || [],
          privateServices: state.services.privateServices,
        },
      };
    case 'load-success-private':
      return {
        ...state,
        isLoadingPrivateApps: action.hasMore,
        errorFetchingPrivateApps: null,
        services: {
          publicServices: state.services.publicServices,
          privateServices: action.privateServices
            ? [...state.services.privateServices, ...action.privateServices]
            : state.services.privateServices,
        },
      };
    case 'load-fail-public':
      return {
        ...state,
        isLoadingPublicApps: false,
        errorFetchingPublicApps: new Error(
          'Unable to successfully load services, try again later.'
        ),
      };
    case 'load-fail-private':
      return {
        ...state,
        isLoadingPrivateApps: false,
        errorFetchingPrivateApps: new Error(
          'Unable to successfully load services, try again later.'
        ),
      };
    default:
      throw new Error(
        // @ts-ignore
        `Unexpected reducer action type ${action.type} in useAllServicesLoader`
      );
  }
};

type UseAllServicesLoaderProps = {
  // If `false` this will filter out private service results from the child.
  // It does not prevent the private service request from occurring.
  includePrivate: boolean;
};

const useAllServicesLoader = ({
  includePrivate,
}: UseAllServicesLoaderProps): ServiceLoaderResults => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const onReceivePublicServices = (publicServices: Service[]) => {
    dispatch({ type: 'load-success-public', publicServices });
  };

  useEffect(() => {
    const onReceivePrivateServices = ({
      results,
      next,
    }: {
      results: Service[];
      next: any;
    }) => {
      dispatch({
        type: 'load-success-private',
        privateServices: results,
        hasMore: Boolean(next),
      });

      if (next) {
        loadPrivateServices(onReceivePrivateServices, next).catch(() => {
          dispatch({ type: 'load-fail-private' });
        });
      }
    };

    dispatch({ type: 'loading' });

    loadPublicServices(onReceivePublicServices).catch(() => {
      dispatch({ type: 'load-fail-public' });
    });

    loadPrivateServices(onReceivePrivateServices).catch(() => {
      dispatch({ type: 'load-fail-private' });
    });
  }, [includePrivate]);

  const { publicServices, privateServices } = state.services;

  const legacyCompatibleServices = combineToLegacyCompatibleServices(
    publicServices,
    privateServices,
    includePrivate
  );

  return {
    error: state.errorFetchingPublicApps || state.errorFetchingPrivateApps,
    loading: Boolean(state.isLoadingPublicApps || state.isLoadingPrivateApps),
    services: legacyCompatibleServices,
  };
};

export default useAllServicesLoader;
